import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../helpers";
import { useCanvasList } from "../tars-hooks";
import {
  CANVAS_LIST_DEFAULT_LIMIT,
  TARS_BACK_TO_NOTEBOOKS_LABEL,
  TARS_LANDING_PATH,
} from "../tars-constants";
import CanvasTypeSelector from "./CanvasTypeSelector";
import AddCanvasModal from "./AddCanvasModal";
import AnalyticsComponent from "./analytics/AnalyticsComponent";
import { ResizeHandle } from "./analytics/ResizeHandle";
import CanvasPane from "./analytics/CanvasPane";

function NotebookMainShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-[#e5e7eb] bg-[#f9fafb] px-4 py-2">
        <Link
          to={TARS_LANDING_PATH}
          className="inline-flex items-center gap-1 rounded-lg px-1 py-0.5 text-[13px] font-semibold text-[#374151] transition hover:bg-white hover:text-[#111827]"
        >
          <ChevronLeft size={16} strokeWidth={2.5} aria-hidden />
          {TARS_BACK_TO_NOTEBOOKS_LABEL}
        </Link>
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-row">{children}</div>
    </div>
  );
}

function NotebookMainContentInner() {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const { notebookId } = useParams<{ notebookId: string }>();
  const nid = notebookId != null ? Number(notebookId) : null;
  const notebookIdNum = nid != null && !Number.isNaN(nid) ? nid : null;

  const { tarsStore, tarsAnalyticsStore } = useStore();
  const { data, isLoading, isSuccess } = useCanvasList(
    notebookIdNum,
    !!notebookIdNum,
    { offset: 0, limit: CANVAS_LIST_DEFAULT_LIMIT }
  );

  const canvases = data?.canvases ?? [];
  const hasCanvases = canvases.length > 0;

  useEffect(() => {
    if (!notebookIdNum) return;
    tarsStore.setSelectedCanvas(null);
  }, [notebookIdNum, tarsStore]);

  useEffect(() => {
    if (!isSuccess || hasCanvases) return;
    tarsStore.setSelectedCanvas(null);
    tarsAnalyticsStore.setAnalyticsTitle("");
  }, [isSuccess, hasCanvases, tarsStore, tarsAnalyticsStore]);

  useEffect(() => {
    if (!hasCanvases || canvases.length === 0) return;
    if (tarsStore.selectedCanvas != null) return;
    const first = canvases[0];
    if (!first) return;
    tarsStore.setSelectedCanvas({ id: first.id, name: first.name });
    tarsAnalyticsStore.setAnalyticsTitle(first.name);
  }, [hasCanvases, canvases, tarsStore, tarsAnalyticsStore]);

  const handleSelectCanvas = (canvas: { id: number; name: string }) => {
    tarsStore.setSelectedCanvas(canvas);
    tarsAnalyticsStore.setAnalyticsTitle(canvas.name);
    tarsAnalyticsStore.setFacets([]);
    tarsAnalyticsStore.setSelectedFacetId(null);
  };

  if (!notebookIdNum) {
    return (
      <NotebookMainShell>
        <div className="flex flex-1 items-center justify-center bg-white text-gray-600">
          Invalid notebook
        </div>
      </NotebookMainShell>
    );
  }

  if (isLoading) {
    return (
      <NotebookMainShell>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-white p-6 text-gray-600">
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          <span>Loading canvases...</span>
        </div>
      </NotebookMainShell>
    );
  }

  if (isSuccess && !hasCanvases) {
    return (
      <NotebookMainShell>
        <CanvasTypeSelector notebookId={notebookIdNum} />
      </NotebookMainShell>
    );
  }

  return (
    <>
      <NotebookMainShell>
        <AnalyticsComponent />
        <ResizeHandle />
        <CanvasPane
          canvases={canvases}
          selectedCanvas={tarsStore.selectedCanvas}
          onSelectCanvas={handleSelectCanvas}
          onAddNewCanvas={() => setAddModalOpen(true)}
        />
      </NotebookMainShell>
      <AddCanvasModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        notebookId={notebookIdNum}
      />
    </>
  );
}

export default observer(NotebookMainContentInner);
